import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header, MobileNav } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { providersAPI } from '../../lib/api';
import { SERVICE_TYPES, CITIES, getCityName } from '../../lib/constants';
import { Plus, Trash, MapPin, Camera, FloppyDisk, Dog, House, Heart, Clock, CalendarBlank } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const DAYS = [
  { id: 0, name: 'Segunda', short: 'Seg' },
  { id: 1, name: 'Terça', short: 'Ter' },
  { id: 2, name: 'Quarta', short: 'Qua' },
  { id: 3, name: 'Quinta', short: 'Qui' },
  { id: 4, name: 'Sexta', short: 'Sex' },
  { id: 5, name: 'Sábado', short: 'Sáb' },
  { id: 6, name: 'Domingo', short: 'Dom' },
];

export default function ProviderProfilePage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);

  const [formData, setFormData] = useState({
    bio: '',
    photos: [],
    services: [],
    zones: [],
    latitude: null,
    longitude: null,
    availability: {},
    weekly_schedule: [],
    date_exceptions: []
  });

  const [newPhotoUrl, setNewPhotoUrl] = useState('');

  const fetchProfile = useCallback(async () => {
    try {
      const profile = await providersAPI.getProviderByUser(user?.user_id);
      setFormData({
        bio: profile.bio || '',
        photos: profile.photos || [],
        services: profile.services || [],
        zones: profile.zones || [],
        latitude: profile.latitude,
        longitude: profile.longitude,
        availability: profile.availability || {},
        weekly_schedule: profile.weekly_schedule || [],
        date_exceptions: profile.date_exceptions || []
      });
      setHasProfile(true);
    } catch (error) {
      // No profile yet
      setHasProfile(false);
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSave = async () => {
    if (formData.zones.length === 0) {
      toast.error('Selecione pelo menos uma zona de atuação');
      return;
    }

    if (formData.services.length === 0) {
      toast.error('Adicione pelo menos um serviço');
      return;
    }

    setSaving(true);
    try {
      await providersAPI.createOrUpdateProfile(formData);
      
      // Update user role if needed
      if (user?.role !== 'prestador') {
        await updateUser({ role: 'prestador' });
      }
      
      toast.success('Perfil guardado!');
      if (!hasProfile) {
        navigate('/provider/dashboard');
      }
    } catch (error) {
      toast.error('Erro ao guardar perfil');
    } finally {
      setSaving(false);
    }
  };

  const toggleZone = (zoneId) => {
    setFormData(prev => ({
      ...prev,
      zones: prev.zones.includes(zoneId)
        ? prev.zones.filter(z => z !== zoneId)
        : [...prev.zones, zoneId]
    }));
  };

  const addService = (serviceType) => {
    if (formData.services.some(s => s.type === serviceType)) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, { type: serviceType, price_per_hour: 0, price_per_day: 0 }]
    }));
  };

  const updateService = (serviceType, field, value) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map(s => 
        s.type === serviceType ? { ...s, [field]: parseFloat(value) || 0 } : s
      )
    }));
  };

  const removeService = (serviceType) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(s => s.type !== serviceType)
    }));
  };

  const addPhoto = () => {
    if (!newPhotoUrl.trim()) return;
    setFormData(prev => ({
      ...prev,
      photos: [...prev.photos, newPhotoUrl.trim()]
    }));
    setNewPhotoUrl('');
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const addWeeklySlot = (dayId) => {
    setFormData(prev => ({
      ...prev,
      weekly_schedule: [...prev.weekly_schedule, { day: dayId, start: '09:00', end: '18:00' }]
    }));
  };

  const updateWeeklySlot = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      weekly_schedule: prev.weekly_schedule.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const removeWeeklySlot = (index) => {
    setFormData(prev => ({
      ...prev,
      weekly_schedule: prev.weekly_schedule.filter((_, i) => i !== index)
    }));
  };

  const addDateException = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];
    setFormData(prev => ({
      ...prev,
      date_exceptions: [...prev.date_exceptions, { date: dateStr, start: '09:00', end: '18:00', available: true }]
    }));
  };

  const updateDateException = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      date_exceptions: prev.date_exceptions.map((e, i) => i === index ? { ...e, [field]: value } : e)
    }));
  };

  const removeDateException = (index) => {
    setFormData(prev => ({
      ...prev,
      date_exceptions: prev.date_exceptions.filter((_, i) => i !== index)
    }));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocalização não suportada');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }));
        toast.success('Localização obtida!');
      },
      (error) => {
        toast.error('Não foi possível obter a localização');
      }
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-6">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="h-48 animate-pulse bg-muted" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="container mx-auto px-4 py-6 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold font-outfit">
              {hasProfile ? 'Editar Perfil' : 'Criar Perfil de Prestador'}
            </h1>
          </div>

          <div className="space-y-6">
            {/* Profile Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Informações Básicas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold">{user?.name}</h3>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Sobre Mim</label>
                  <Textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Conte um pouco sobre si é a sua experiência com animais..."
                    rows={4}
                    data-testid="bio-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Zones */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Zonas de Atuação
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {CITIES.map((city) => (
                    <div
                      key={city.id}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.zones.includes(city.id)
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => toggleZone(city.id)}
                      data-testid={`zone-${city.id}`}
                    >
                      <Checkbox checked={formData.zones.includes(city.id)} />
                      <span className="text-sm font-medium">{city.name}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-4">
                  <Button 
                    variant="outline" 
                    onClick={getCurrentLocation}
                    className="rounded-full"
                    type="button"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Usar Minha Localização
                  </Button>
                  {formData.latitude && formData.longitude && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Localização: {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Services */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Dog className="w-5 h-5" />
                  Serviços e Preços
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Available services to add */}
                <div className="flex flex-wrap gap-2">
                  {SERVICE_TYPES.filter(s => !formData.services.some(fs => fs.type === s.id)).map((service) => (
                    <Button
                      key={service.id}
                      variant="outline"
                      size="sm"
                      onClick={() => addService(service.id)}
                      className="rounded-full"
                      data-testid={`add-service-${service.id}`}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {service.name}
                    </Button>
                  ))}
                </div>

                {/* Added services */}
                {formData.services.map((service) => {
                  const serviceInfo = SERVICE_TYPES.find(s => s.id === service.type);
                  return (
                    <div key={service.type} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">{serviceInfo?.name}</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeService(service.type)}
                          className="text-destructive"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-muted-foreground">Preço por Hora (€)</label>
                          <Input
                            type="number"
                            value={service.price_per_hour || ''}
                            onChange={(e) => updateService(service.type, 'price_per_hour', e.target.value)}
                            placeholder="0"
                            data-testid={`price-hour-${service.type}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Preço por Dia (€)</label>
                          <Input
                            type="number"
                            value={service.price_per_day || ''}
                            onChange={(e) => updateService(service.type, 'price_per_day', e.target.value)}
                            placeholder="0"
                            data-testid={`price-day-${service.type}`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {formData.services.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Adicione pelo menos um serviço clicando nos botões acima
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Weekly Availability */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Horário Semanal
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2 mb-3">
                  {DAYS.map(day => {
                    const hasSlot = formData.weekly_schedule.some(s => s.day === day.id);
                    return (
                      <Button
                        key={day.id}
                        variant={hasSlot ? "default" : "outline"}
                        size="sm"
                        onClick={() => !hasSlot && addWeeklySlot(day.id)}
                        className="rounded-full"
                        disabled={hasSlot}
                        data-testid={`add-day-${day.id}`}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {day.short}
                      </Button>
                    );
                  })}
                </div>

                {formData.weekly_schedule.length > 0 ? (
                  <div className="space-y-3">
                    {formData.weekly_schedule
                      .sort((a, b) => a.day - b.day)
                      .map((slot, index) => {
                        const dayInfo = DAYS.find(d => d.id === slot.day);
                        return (
                          <div key={`${slot.day}-${index}`} className="flex items-center gap-3 p-3 border rounded-lg" data-testid={`schedule-slot-${slot.day}`}>
                            <span className="font-medium text-sm w-16">{dayInfo?.short}</span>
                            <Input
                              type="time"
                              value={slot.start}
                              onChange={(e) => updateWeeklySlot(index, 'start', e.target.value)}
                              className="w-28"
                            />
                            <span className="text-muted-foreground text-sm">até</span>
                            <Input
                              type="time"
                              value={slot.end}
                              onChange={(e) => updateWeeklySlot(index, 'end', e.target.value)}
                              className="w-28"
                            />
                            <Button variant="ghost" size="sm" onClick={() => removeWeeklySlot(index)} className="text-destructive ml-auto">
                              <Trash className="w-4 h-4" />
                            </Button>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-3">
                    Clique num dia para adicionar o seu horário semanal
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Date Exceptions */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarBlank className="w-5 h-5" />
                    Exceções por Data
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={addDateException} className="rounded-full" data-testid="add-date-exception-btn">
                    <Plus className="w-3 h-3 mr-1" /> Adicionar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {formData.date_exceptions.length > 0 ? (
                  formData.date_exceptions.map((exc, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 border rounded-lg" data-testid={`date-exception-${index}`}>
                      <Input
                        type="date"
                        value={exc.date}
                        onChange={(e) => updateDateException(index, 'date', e.target.value)}
                        className="w-40"
                      />
                      <Select value={exc.available ? 'available' : 'unavailable'} onValueChange={(v) => updateDateException(index, 'available', v === 'available')}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="available">Disponível</SelectItem>
                          <SelectItem value="unavailable">Indisponível</SelectItem>
                        </SelectContent>
                      </Select>
                      {exc.available && (
                        <>
                          <Input type="time" value={exc.start} onChange={(e) => updateDateException(index, 'start', e.target.value)} className="w-28" />
                          <span className="text-muted-foreground text-sm">até</span>
                          <Input type="time" value={exc.end} onChange={(e) => updateDateException(index, 'end', e.target.value)} className="w-28" />
                        </>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => removeDateException(index)} className="text-destructive ml-auto">
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-3">
                    Adicione exceções para datas específicas (ex: férias, dias extra)
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Photos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Fotos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newPhotoUrl}
                    onChange={(e) => setNewPhotoUrl(e.target.value)}
                    placeholder="URL da foto..."
                    data-testid="photo-url-input"
                  />
                  <Button onClick={addPhoto} variant="outline" data-testid="add-photo-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {formData.photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-4">
                    {formData.photos.map((photo, index) => (
                      <div key={photo} className="relative aspect-square rounded-lg overflow-hidden group">
                        <img src={photo} alt="" className="w-full h-full object-cover" />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removePhoto(index)}
                        >
                          <Trash className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full rounded-full h-12"
              data-testid="save-profile-btn"
            >
              {saving ? 'A guardar...' : (
                <>
                  <FloppyDisk className="w-4 h-4 mr-2" />
                  {hasProfile ? 'Guardar Alterações' : 'Criar Perfil'}
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}
